import type { ClientHydraItem } from "@verbleif/lib/src/composables/Api/Type/Client/client.interface";
import type { LocationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Location/location.interface";
import type { SpaceHydraItem } from "@verbleif/lib/src/composables/Api/Type/Space/space.interface";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { createGlobalState, useLocalStorage, useSessionStorage } from "@vueuse/core";

import { computed, ref, watch } from "vue";
import { useAccountsStore } from "../AccountsStore/useAccountsStore";

export const useLocationStore = createGlobalState(
  () => {
    const mode = ref<"location" | "client" | "global">("location");

    function switchMode(newMode: "location" | "client" | "global") {
      mode.value = newMode;
    }

    const locations = ref<Record<string, LocationHydraItem>>({});
    const clients = ref<Record<string, ClientHydraItem>>({});
    const spaces = ref<SpaceHydraItem[]>([]);
    /** Both lists load in parallel; validating against a list that has not arrived yet wipes the saved location. */
    const locationsLoaded = ref(false);
    const spacesLoaded = ref(false);
    const isCatalogLoaded = computed(() => locationsLoaded.value && spacesLoaded.value);
    const defaultLocation = useLocalStorage<string | null>(useAccountsStore().scopedKey("defaultLocation"), null);
    const selectedLocation = useSessionStorage<string | null>("selectedLocation", null);

    const clientsList = computed<ClientHydraItem[]>(() => {
      return Object.values(clients.value);
    });

    const locationsList = computed<LocationHydraItem[]>(() => {
      if (!locations.value) {
        return [];
      }
      return Object.values(locations.value);
    });

    const selectedLocationId = computed<number | null>(() => {
      return selectedLocation.value ? iriToId(selectedLocation.value) : null;
    });

    const hasMultipleLocations = computed<boolean>(() => {
      if (!spaces.value) {
        return false;
      }
      return spaces.value.length > 1;
    });

    const modeSelectedLocation = computed<string | null>(() => {
      if (mode.value !== "location") {
        return null;
      }

      if (!selectedLocation.value) {
        return null;
      }
      return selectedLocation.value;
    });

    const selectedLocationObject = computed<LocationHydraItem | null>(() => {
      if (!selectedLocation.value) {
        return null;
      }
      return locations.value?.[selectedLocation.value] || null;
    });

    const modeSelectedClient = computed<string | null>(() => {
      if (mode.value !== "client") {
        return null;
      }

      if (!selectedLocationObject.value) {
        return null;
      }
      return selectedLocationObject.value.client || null;
    });

    const getSelectedLocationId = computed<number | null>(() => {
      return selectedLocationObject.value?.id || null;
    });

    function getSpaceByLocation(location: string): SpaceHydraItem | null {
      return spaces.value.find(s => s.location === location) || null;
    }

    function isSpaceValid(space: SpaceHydraItem): boolean {
      return !!space.location && !!space.accepted && !space.expired && space.active;
    }

    function updateSpaceState() {
      if (!isCatalogLoaded.value) {
        return;
      }

      // If the selectedLocation does not exist anymore somehow
      if (selectedLocation.value && !locations.value?.[selectedLocation.value]) {
        setSelectedLocation(null);
      }

      if (defaultLocation.value && !locations.value?.[defaultLocation.value]) {
        setDefaultLocation(null);
      }

      if (defaultLocation.value && !selectedLocation.value) {
        const space = getSpaceByLocation(defaultLocation.value);
        if (space && isSpaceValid(space)) {
          setSelectedLocation(defaultLocation.value);
        }
      } else if (spaces.value.length === 1 && spaces.value[0].location && selectedLocation.value !== spaces.value[0].location) {
        const space = getSpaceByLocation(spaces.value[0].location);
        if (space && isSpaceValid(space)) {
          setSelectedLocation(spaces.value[0].location);
          setDefaultLocation(spaces.value[0].location);
        }
      }
    }

    const pendingInvites = computed<number | null>(() => {
      const unAcceptedSpaces = spaces.value.filter(s => !s.accepted && s.active && !s.expired);
      return unAcceptedSpaces.length > 0 ? unAcceptedSpaces.length : null;
    });

    const hasAnyValidSpace = computed<boolean>(() => spaces.value.some(isSpaceValid));

    function setImportRunningForLocationIri(iri: string, isRunning: boolean): void {
      if (!locations.value) {
        console.error("Cant set setImportRunningForLocationId since there are no locations");
        return;
      }
      const index = locations.value[iri];
      if (!index) {
        return;
      }
      locations.value[iri].importRunning = isRunning;
    }

    function setDefaultLocation(val: string | null): void {
      defaultLocation.value = val;
    }

    function setSelectedLocation(val: string | null): void {
      selectedLocation.value = val;
    }

    // Sync selectedLocation with defaultLocation when selectedLocation is null or invalid
    watch(
      [defaultLocation, selectedLocation, spaces, locations, isCatalogLoaded],
      () => {
        if (!isCatalogLoaded.value) {
          return;
        }

        // Check if selectedLocation is invalid (doesn't exist in locations)
        const isSelectedLocationInvalid = selectedLocation.value && !locations.value?.[selectedLocation.value];

        // Apply default location if:
        // 1. We have a default location
        // 2. Either selectedLocation is null OR it's invalid
        if (defaultLocation.value && (!selectedLocation.value || isSelectedLocationInvalid) && spaces.value.length > 0) {
          const space = getSpaceByLocation(defaultLocation.value);
          if (space && isSpaceValid(space)) {
            setSelectedLocation(defaultLocation.value);
          }
        }
      },
      { immediate: true },
    );

    function reset() {
      setSelectedLocation(null);
      locations.value = {};
      // Don't clear defaultLocation - it should persist across logouts
      clients.value = {};
      spaces.value = [];
      locationsLoaded.value = false;
      spacesLoaded.value = false;
    }

    const hasSingleLocation = computed<boolean>(() => {
      return locationsList.value.length === 1;
    });

    const hasMoreThanOneLocation = computed<boolean>(() => {
      return spaces.value.length > 1;
    });

    function getLocationsForClient(clientIri: string | null | undefined): LocationHydraItem[] {
      if (!clientIri) {
        return locationsList.value;
      }
      return locationsList.value.filter(l => l.client === clientIri);
    }

    function clientHasSingleLocation(clientIri: string | null | undefined): boolean {
      if (!clientIri) {
        return hasSingleLocation.value;
      }
      return getLocationsForClient(clientIri).length === 1;
    }

    const selectedClient = computed<ClientHydraItem | null>(() => {
      if (!selectedLocationObject.value) {
        return null;
      }
      const clientIri = selectedLocationObject.value.client;

      if (!clientIri) {
        return null;
      }

      return clients.value[clientIri] || null;
    });

    const selectedClientHasSingleLocation = computed<boolean>(() => {
      return clientHasSingleLocation(selectedClient.value?.["@id"] ?? null);
    });

    return {
      locationsList,
      locations,
      mode,
      modeSelectedClient,
      spaces,
      locationsLoaded,
      spacesLoaded,
      isCatalogLoaded,
      selectedClient,
      clientsList,
      switchMode,
      clients,
      modeSelectedLocation,
      selectedLocation,
      selectedLocationId,
      hasSingleLocation,
      hasMoreThanOneLocation,
      selectedLocationObject,
      pendingInvites,
      hasAnyValidSpace,
      hasMultipleLocations,
      getLocationsForClient,
      clientHasSingleLocation,
      selectedClientHasSingleLocation,
      getSelectedLocationId,
      reset,
      defaultLocation,
      setSelectedLocation,
      setDefaultLocation,
      setImportRunningForLocationIri,
      updateSpaceState,
    };
  },
);
